// TagInferenceRoutine / DocRequestRelevanceRoutine output schemas.

export enum RelevanceLabel {
    HIGHLY_RELEVANT = "HIGHLY_RELEVANT",
    RELEVANT = "RELEVANT",
    NOT_RELEVANT = "NOT_RELEVANT",
}

export type RelevanceLevel = "Hot" | "Warm" | "Cold";

export const RelevanceRanges: Record<RelevanceLevel, { min: number; max: number }> = {
    Cold: {min: -1.0, max: -0.0001},
    Warm: {min: 0.0, max: 0.4999},
    Hot: {min: 0.5, max: 1.0},
};

export const RelevanceDefaults: Record<RelevanceLevel, number> = {
    Cold: -0.9,
    Warm: 0.2,
    Hot: 0.95,
};

export type EntityPresenceLevel = "Include" | "Exclude";

export const EntityPresenceRanges: Record<EntityPresenceLevel, { min: number; max: number }> = {
    Include: {min: 0.5, max: 1.0},
    Exclude: {min: -1.0, max: -0.0001},
};

export const EntityPresenceDefaults: Record<EntityPresenceLevel, number> = {
    Include: 1.0,
    Exclude: -1.0,
};

export function getEntityPresenceLevelFromScoreRange(
    min: number,
    max: number
): EntityPresenceLevel | null {
    const tolerance = 1e-4;
    for (const [label, range] of Object.entries(EntityPresenceRanges)) {
        if (
            Math.abs(range.min - min) < tolerance &&
            Math.abs(range.max - max) < tolerance
        ) {
            return label as EntityPresenceLevel;
        }
    }
    if (max >= 0.5) return "Include";
    if (max <= 0.0) return "Exclude";
    return null;
}

export const LabelToLevel: Record<RelevanceLabel, RelevanceLevel> = {
    [RelevanceLabel.HIGHLY_RELEVANT]: "Hot",
    [RelevanceLabel.RELEVANT]: "Warm",
    [RelevanceLabel.NOT_RELEVANT]: "Cold",
};

export const LevelToLabel: Record<RelevanceLevel, RelevanceLabel> = {
    Hot: RelevanceLabel.HIGHLY_RELEVANT,
    Warm: RelevanceLabel.RELEVANT,
    Cold: RelevanceLabel.NOT_RELEVANT,
};

export function getRelevanceScore(label: RelevanceLabel): number {
    const level = LabelToLevel[label];
    return RelevanceDefaults[level];
}

export function getRelevanceLevelFromScore(score?: number): RelevanceLevel {
    if (score === null || score === undefined) return "Cold";
    if (score >= 0.5) return "Hot";
    if (score >= 0.0) return "Warm";
    return "Cold";
}

export function getRelevanceLabelFromScore(score?: number): RelevanceLabel {
    if (score === null || score === undefined) return RelevanceLabel.NOT_RELEVANT;
    const level = getRelevanceLevelFromScore(score);
    return LevelToLabel[level];
}

export function getScoreRangeFromLabel(label: RelevanceLevel) {
    return RelevanceRanges[label];
}

export function getLabelFromScoreRange(
    min: number,
    max: number
): RelevanceLevel | null {
    const tolerance = 1e-4;
    for (const [label, range] of Object.entries(RelevanceRanges)) {
        if (
            Math.abs(range.min - min) < tolerance &&
            Math.abs(range.max - max) < tolerance
        ) {
            return label as RelevanceLevel;
        }
    }
    if (max >= 0.5) return "Hot";
    if (max > 0.0) return "Warm";
    if (max <= 0.0) return "Cold";
    return null;
}

export function isRelevant(label: RelevanceLabel): boolean {
    return (
        label === RelevanceLabel.HIGHLY_RELEVANT ||
        label === RelevanceLabel.RELEVANT
    );
}

export enum QuoteSource {
    OWN_BODY = "OWN_BODY",
    METADATA = "METADATA",
    EMBEDDING = "EMBEDDING",
}

export type EvidenceSpan = {
    quote: string;
    explanation: string;
    source_hint?: QuoteSource | null;
    quote_source?: QuoteSource | null;
    validated?: boolean;
};

export type TagRelevanceEntry = {
    relevance: RelevanceLabel;
    evidence: EvidenceSpan[];
};

export type RelevanceResponse = {
    relevance_map: Record<string, TagRelevanceEntry>;
};
